import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import { databaseApi } from "@/lib/api";
import { DatabaseDetails as DatabaseDetailsType } from "@/types/database";
import {
  AlertOctagon,
  AlertTriangle,
  Copy,
  ExternalLink,
  Eye,
  EyeOff,
  HardDrive,
  Key,
  Plus,
  RefreshCw,
  RotateCcw,
  ShieldQuestionIcon,
  Trash2,
  Users,
  X
} from "lucide-react";
import React, { useEffect, useState } from "react";
import MongoLeafIcon from "./icons/MongoLeafIcon";
import MarkForDeletionDialog from "./MarkForDeletionDialog";
import ForceDeleteDialog from "./ForceDeleteDialog";

interface DatabaseDetailsProps {
  database: DatabaseDetailsType;
  onClose: () => void;
  onRefresh: () => void;
  onUpdateDatabase?: (database: DatabaseDetailsType) => void;
  onDelete?: (dbname: string) => Promise<void>;
  onUndoDelete?: (dbname: string) => Promise<void>;
  onForceDelete?: (dbname: string) => Promise<void>;
}

interface ConnectionStrings {
  sa: string;
  rw: string;
  ro: string;
  example: string;
}

interface UsageInfo {
  used: number;
  total: number;
  percentage: number;
  status: "low" | "medium" | "high";
}

const DatabaseDetails: React.FC<DatabaseDetailsProps> = ({
  database,
  onClose,
  onRefresh,
  onUpdateDatabase,
  onDelete,
  onUndoDelete,
  onForceDelete,
}) => {
  const [connectionStrings, setConnectionStrings] =
    useState<ConnectionStrings | null>(null);
  const [shadowConnectionStrings, setShadowConnectionStrings] =
    useState<ConnectionStrings | null>(null);
  const [usageInfo, setUsageInfo] = useState<UsageInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [showCapacityForm, setShowCapacityForm] = useState(false);
  const [newSize, setNewSize] = useState(database.size);
  const [showPasswords, setShowPasswords] = useState<{
    [key: string]: boolean;
  }>({});
  const [showShadowPasswords, setShowShadowPasswords] = useState<{
    [key: string]: boolean;
  }>({});
  const [showConnectionPasswords, setShowConnectionPasswords] = useState<{
    [key: string]: boolean;
  }>({});
  const [showShadowConnectionPasswords, setShowShadowConnectionPasswords] =
    useState<{
      [key: string]: boolean;
    }>({});
  const [showRegenerateConfirm, setShowRegenerateConfirm] = useState(false);
  const [tags, setTags] = useState<string[]>(database.tags || []);
  const [tagInput, setTagInput] = useState<string>("");
  const [showMarkForDeletionDialog, setShowMarkForDeletionDialog] = useState(false);
  const [showForceDeleteDialog, setShowForceDeleteDialog] = useState(false);
  const [isUndoingDelete, setIsUndoingDelete] = useState(false);
  const [isForceDeleting, setIsForceDeleting] = useState(false);
  const { toast } = useToast();

  const [shadowDatabaseDetails, setShadowDatabaseDetails] = useState<{
    sa_username: string;
    sa_password: string;
  }>({
    sa_username: "",
    sa_password: "",
  });

  // Update tags when database changes
  useEffect(() => {
    setTags(database.tags || []);
  }, [database.tags]);

  const getDatabaseIcon = (dbtype: string) => {
    switch (dbtype) {
      case "MYSQL":
        return "🐬";
      case "POSTGRES":
        return "🐘";
      case "MONGODB":
        return <MongoLeafIcon className="w-6 h-6" />;
      default:
        return "🗄️";
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return "text-green-600";
      case "pending_delete":
        return "text-yellow-600";
      case "deleted":
        return "text-red-600";
      default:
        return "text-gray-600";
    }
  };

  const getUsageColor = (status: "low" | "medium" | "high") => {
    switch (status) {
      case "low":
        return "bg-green-500";
      case "medium":
        return "bg-yellow-500";
      case "high":
        return "bg-red-500";
      default:
        return "bg-gray-500";
    }
  };

  const getUsageWarning = (
    status: "low" | "medium" | "high",
    percentage: number
  ) => {
    if (status === "high" && percentage >= 90) {
      return "Critical: Database capacity nearly full";
    } else if (status === "high") {
      return "Warning: High database usage";
    } else if (status === "medium") {
      return "Moderate: Consider monitoring usage";
    }
    return "Good: Healthy usage levels";
  };

  const handleAddTag = async () => {
    if (tagInput.trim() && !tags.includes(tagInput.trim())) {
      const newTags = [...tags, tagInput.trim()];
      setTags(newTags);
      setTagInput("");

      // Auto-save to backend
      try {
        const response = await databaseApi.updateDatabaseTags(
          database.dbname,
          newTags
        );
        if (response.success) {
          toast({
            title: "Tag Added",
            description: "Tag has been added successfully.",
            status: "success",
          });
          if (onUpdateDatabase) {
            onUpdateDatabase({ ...database, tags: newTags });
          }
        }
      } catch (error) {
        // Revert the change if save failed
        setTags(tags);
        toast({
          title: "Error",
          description: "Failed to add tag. Please try again.",
          status: "error",
        });
      }
    }
  };

  const handleRemoveTag = async (tagToRemove: string) => {
    const newTags = tags.filter((tag) => tag !== tagToRemove);
    setTags(newTags);

    // Auto-save to backend
    try {
      const response = await databaseApi.updateDatabaseTags(
        database.dbname,
        newTags
      );
      if (response.success) {
        toast({
          title: "Tag Removed",
          description: "Tag has been removed successfully.",
          status: "success",
        });
        if (onUpdateDatabase) {
          onUpdateDatabase({ ...database, tags: newTags });
        }
      }
    } catch (error) {
      // Revert the change if save failed
      setTags(tags);
      toast({
        title: "Error",
        description: "Failed to remove tag. Please try again.",
        status: "error",
      });
    }
  };

  const handleTagKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddTag();
    }
  };

  const togglePasswordVisibility = (key: string) => {
    setShowPasswords((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const toggleShadowPasswordVisibility = (key: string) => {
    setShowShadowPasswords((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const toggleConnectionPasswordVisibility = (key: string) => {
    setShowConnectionPasswords((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const toggleShadowConnectionPasswordVisibility = (key: string) => {
    setShowShadowConnectionPasswords((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const maskPassword = (password: string) => {
    if (password.length <= 4) return "*".repeat(password.length);
    return (
      password.substring(0, 2) +
      "*".repeat(password.length - 4) +
      password.substring(password.length - 2)
    );
  };

  const maskConnectionString = (
    connectionString: string,
    showPassword: boolean = false
  ) => {
    if (!connectionString) return connectionString;

    // Handle different database types
    if (connectionString.includes("postgresql://")) {
      return maskPostgresConnectionString(connectionString, showPassword);
    } else if (connectionString.includes("mysql://")) {
      return maskMySQLConnectionString(connectionString, showPassword);
    } else if (connectionString.includes("mongodb://")) {
      return maskMongoConnectionString(connectionString, showPassword);
    }

    return connectionString;
  };

  const maskPostgresConnectionString = (
    connectionString: string,
    showPassword: boolean
  ) => {
    // postgresql://username:password@host:port/database
    const regex = /^(postgresql:\/\/)([^:]+):([^@]+)@(.+)$/;
    const match = connectionString.match(regex);

    if (match) {
      const [, protocol, username, password, rest] = match;
      const maskedPassword = showPassword ? password : "******";
      return `${protocol}${username}:${maskedPassword}@${rest}`;
    }

    return connectionString;
  };

  const maskMySQLConnectionString = (
    connectionString: string,
    showPassword: boolean
  ) => {
    // mysql://username:password@host:port/database
    const regex = /^(mysql:\/\/)([^:]+):([^@]+)@(.+)$/;
    const match = connectionString.match(regex);

    if (match) {
      const [, protocol, username, password, rest] = match;
      const maskedPassword = showPassword ? password : "******";
      return `${protocol}${username}:${maskedPassword}@${rest}`;
    }

    return connectionString;
  };

  const maskMongoConnectionString = (
    connectionString: string,
    showPassword: boolean
  ) => {
    // mongodb://username:password@host:port/database
    const regex = /^(mongodb:\/\/)([^:]+):([^@]+)@(.+)$/;
    const match = connectionString.match(regex);

    if (match) {
      const [, protocol, username, password, rest] = match;
      const maskedPassword = showPassword ? password : "******";
      return `${protocol}${username}:${maskedPassword}@${rest}`;
    }

    return connectionString;
  };

  const fetchConnectionStrings = async () => {
    try {
      const response = await databaseApi.getConnectionStrings(database.dbname);

      if (response.success && response.data) {
        setConnectionStrings(response.data.connectionStrings);
        setShadowConnectionStrings(response.data.shadowDbConnectionStrings);
      } else {
        toast({
          title: "Error",
          description: response.error || "Failed to fetch connection strings",
          status: "error",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch connection strings",
        status: "error",
      });
    }
  };

  const fetchShadowDatabaseDetails = async () => {
    try {
      const response = await databaseApi.getDatabaseByName(
        `${database.dbname}-shadow`
      );

      if (response.success && response.data) {
        setShadowDatabaseDetails({
          sa_username: response.data.database.sa_username,
          sa_password: response.data.database.sa_password,
        });
      } else {
        toast({
          title: "Error",
          description:
            response.error || "Failed to fetch shadow database details",
          status: "error",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch shadow database details",
        status: "error",
      });
    }
  };

  const loadUsageInfo = async () => {
    try {
      const response = await databaseApi.getDatabaseUsage(database.dbname);
      if (response.success && response.data) {
        setUsageInfo(response.data.usage);
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load usage Info",
        status: "error",
      });
    }
  };

  const updateCapacity = async () => {
    setLoading(true);
    try {
      const response = await databaseApi.updateDatabaseCapacity(
        database.dbname,
        newSize
      );
      if (response.success && response.data) {
        setShowCapacityForm(false);
        toast({
          title: "Success",
          description: response.data.message || "Capacity updated successfully",
          status: "success",
        });

        // Update the local database state with the new data
        if (response.data.database) {
          const updatedDatabase = response.data.database;

          // Use the optimized update method if available
          if (onUpdateDatabase) {
            onUpdateDatabase(updatedDatabase);
          } else {
            // Fallback to full refresh
            onRefresh();
          }
        }
      } else {
        toast({
          title: "Error",
          description: response.error || "Failed to update capacity",
          status: "error",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update capacity",
        status: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const regeneratePasswords = async () => {
    setLoading(true);
    try {
      const response = await databaseApi.regeneratePasswords(database.dbname);

      if (response.success && response.data) {
        setShowRegenerateConfirm(false);
        toast({
          title: "Success",
          description:
            "Passwords regenerated successfully. Please update your applications with the new credentials.",
          status: "success",
        });

        // Refresh the database details to get updated passwords
        onRefresh();

        // Refresh connection strings since passwords changed
        await fetchConnectionStrings();
      } else {
        console.log("response", response);

        toast({
          title: "Error",
          description: response.error || "Failed to regenerate passwords",
          status: "error",
        });
      }
    } catch (error) {
      console.log("failed password regenerate");
      toast({
        title: "Error",
        description: "Failed to regenerate passwords",
        status: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({
        title: "Copied!",
        description: `${field} copied to clipboard`,
        status: "success",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to copy to clipboard",
        status: "error",
      });
    }
  };

  const createShadowDatabase = async () => {
    try {
      const response = await databaseApi.createShadowDatabase(database.dbname);

      console.log("response", response);

      if (response.success && response.data) {
        toast({
          title: "Success",
          description: "Shadow Database created Successfully",
          status: "success",
        });

        setShadowDatabaseDetails({
          sa_username: response.data.database.sa_username,
          sa_password: response.data.database.sa_password,
        });
      } else {
        toast({
          title: "Error",
          description: response.error || "Failed to create Shadow Database",
          status: "error",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to create Shadow Database",
        status: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handles marking the database for deletion
   */
  const handleMarkForDeletion = async () => {
    if (!onDelete) return;
    
    try {
      await onDelete(database.dbname);
      setShowMarkForDeletionDialog(false);
      onRefresh();
      onClose(); // Close the details modal after marking for deletion
    } catch (error) {
      // Error handling is done in the parent component
    }
  };

  /**
   * Handles canceling the database deletion
   */
  const handleUndoDelete = async () => {
    if (!onUndoDelete) return;
    
    setIsUndoingDelete(true);
    try {
      await onUndoDelete(database.dbname);
      onRefresh();
      toast({
        title: "Success",
        description: "Database deletion cancelled successfully",
        status: "success",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to cancel deletion",
        status: "error",
      });
    } finally {
      setIsUndoingDelete(false);
    }
  };

  /**
   * Handles the force delete confirmation by calling the API directly
   * and closing both the force delete dialog and database details modal
   */
  const handleForceDeleteConfirm = async () => {
    setIsForceDeleting(true);
    try {
      const response = await databaseApi.forceDelete(database.dbname);
      if (response.success) {
        toast({
          title: "Database Deleted",
          description: response.data?.message || `Database '${database.dbname}' has been permanently deleted`,
          status: "success",
        });
        // Close the force delete dialog
        setShowForceDeleteDialog(false);
        // Refresh the database list
        onRefresh();
        // Close the details modal after force deletion
        onClose();
      } else {
        toast({
          title: "Error",
          description: response.error || "Failed to force delete database",
          status: "error",
        });
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error?.message || "Failed to force delete database",
        status: "error",
      });
    } finally {
      setIsForceDeleting(false);
    }
  };

  useEffect(() => {
    fetchConnectionStrings();
    {
      database.databaseClass == "DEV" && fetchShadowDatabaseDetails();
    }
    loadUsageInfo();
  }, [database.dbname]);

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <Card className="w-full max-w-6xl max-h-[90vh] flex flex-col">
          <CardHeader className="sticky top-0 bg-white z-10 border-b shadow-sm flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-3xl">
                  {getDatabaseIcon(database.dbtype)}
                </span>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <CardTitle className="text-2xl">
                      {database.dbname.replace(/-[^-]+$/, "")}
                    </CardTitle>
                    {/* Tags Display and Editing */}
                    <div className="flex items-center gap-2">
                      {tags.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {tags.map((tag, index) => (
                            <div
                              key={index}
                              className="flex items-center gap-1 bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-sm"
                            >
                              <span>{tag}</span>
                              <button
                                type="button"
                                onClick={() => handleRemoveTag(tag)}
                                className="text-blue-600 hover:text-blue-800"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      {/* Add Tag Input */}
                      <div className="relative">
                        <Input
                          type="text"
                          placeholder="Add tag..."
                          value={tagInput}
                          onChange={(e) => setTagInput(e.target.value)}
                          onKeyPress={handleTagKeyPress}
                          className="w-32 h-8 text-sm"
                        />
                        {tagInput.trim() && (
                          <button
                            type="button"
                            onClick={handleAddTag}
                            className="absolute right-1 top-1/2 transform -translate-y-1/2 text-blue-600 hover:text-blue-800"
                          >
                            <Plus className="h-3 w-3" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                  <p className={`text-sm ${getStatusColor(database.status)}`}>
                    {database.status.replace("_", " ").toUpperCase()}
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onRefresh}
                  disabled={loading}
                >
                  <RefreshCw className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="sm" onClick={onClose}>
                  ✕
                </Button>
              </div>
            </div>
          </CardHeader>

          <CardContent className="space-y-6 overflow-y-auto flex-1 pt-6">
            {/* Database Details at Top */}
            <div className="space-y-3">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="font-medium">Database Type:</span>
                  <p className="capitalize">{database.dbtype}</p>
                </div>
                <div>
                  <span className="font-medium">Size:</span>
                  <p>{database.size}</p>
                </div>
                <div>
                  <span className="font-medium">Created:</span>
                  <p>{new Date(database.created_at).toLocaleDateString()}</p>
                </div>
                <div>
                  <span className="font-medium">Created By:</span>
                  <p>{database.created_by}</p>
                </div>
              </div>
              {database.notes && (
                <div className="text-sm">
                  <span className="font-medium">Notes:</span>
                  <p className="mt-1 text-gray-800">{database.notes}</p>
                </div>
              )}
            </div>

            {/* Tags Section */}
            {/* This section is now moved to the header */}

            {/* Usage & Capacity */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <HardDrive className="w-5 h-5" />
                <h3 className="text-lg font-semibold">Usage & Capacity</h3>
              </div>

              {usageInfo ? (
                <div className="space-y-3">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span>Storage Usage</span>
                      <span>{usageInfo.percentage.toFixed(1)}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${getUsageColor(
                          usageInfo.status
                        )}`}
                        style={{ width: `${usageInfo.percentage}%` }}
                      ></div>
                    </div>
                    <p className="text-xs text-gray-600 mt-1">
                      {getUsageWarning(usageInfo.status, usageInfo.percentage)}
                    </p>
                  </div>
                  <div className="flex justify-end gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">Used:</span>
                      <span className="ml-2 font-medium">
                        {(usageInfo.used / 1024 / 1024 / 1024).toFixed(2)} GB
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600">Total:</span>
                      <span className="ml-2 font-medium">
                        {(usageInfo.total / 1024 / 1024 / 1024).toFixed(2)} GB
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-gray-500">Loading usage info...</p>
              )}

              {/* Capacity increase functionality temporarily hidden
              {showCapacityForm ? (
                <div className="space-y-3 p-3 bg-gray-50 rounded-lg">
                  <div className="space-y-2">
                    <Label htmlFor="newSize">New Size (MB/GB/TB)</Label>
                    <div className="flex gap-2">
                      <Input
                        id="newSize"
                        value={newSize}
                        onChange={(e) => setNewSize(e.target.value)}
                        placeholder="e.g., 2GB"
                      />
                      <Button
                        onClick={updateCapacity}
                        disabled={loading}
                      >
                        {loading ? "Updating..." : "Update"}
                      </Button>
                    </div>
                  </div>
                </div>
              ) : (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setShowCapacityForm(true)}
                >
                  Increase Capacity
                </Button>
              )}
              */}
            </div>

            <div className="border-t border-gray-200"></div>

            {/* User Credentials */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  <h3 className="text-lg font-semibold">User Credentials</h3>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setShowRegenerateConfirm(true)}
                  disabled={loading}
                  className="flex items-center gap-2"
                >
                  <Key className="w-4 h-4" />
                  Regenerate Passwords
                </Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Super Admin</Label>
                  <div className="space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-mono bg-muted px-2 py-1 rounded">
                        {database.sa_username}
                      </span>
                      <Button
                        size="sm"
                        onClick={() =>
                          copyToClipboard(
                            database.sa_username,
                            "Super Admin username"
                          )
                        }
                      >
                        <Copy className="w-3 h-3" />
                      </Button>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-mono bg-muted px-2 py-1 rounded">
                        {showPasswords.sa
                          ? database.sa_password
                          : maskPassword(database.sa_password)}
                      </span>
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => togglePasswordVisibility("sa")}
                        >
                          {showPasswords.sa ? (
                            <EyeOff className="w-3 h-3" />
                          ) : (
                            <Eye className="w-3 h-3" />
                          )}
                        </Button>
                        <Button
                          size="sm"
                          onClick={() =>
                            copyToClipboard(
                              database.sa_password,
                              "Super Admin password"
                            )
                          }
                        >
                          <Copy className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium">Read/Write</Label>
                  <div className="space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-mono bg-muted px-2 py-1 rounded">
                        {database.rw_username}
                      </span>
                      <Button
                        size="sm"
                        onClick={() =>
                          copyToClipboard(
                            database.rw_username,
                            "Read/Write username"
                          )
                        }
                      >
                        <Copy className="w-3 h-3" />
                      </Button>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-mono bg-muted px-2 py-1 rounded">
                        {showPasswords.rw
                          ? database.rw_password
                          : maskPassword(database.rw_password)}
                      </span>
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => togglePasswordVisibility("rw")}
                        >
                          {showPasswords.rw ? (
                            <EyeOff className="w-3 h-3" />
                          ) : (
                            <Eye className="w-3 h-3" />
                          )}
                        </Button>
                        <Button
                          size="sm"
                          onClick={() =>
                            copyToClipboard(
                              database.rw_password,
                              "Read/Write password"
                            )
                          }
                        >
                          <Copy className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium">Read Only</Label>
                  <div className="space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-mono bg-muted px-2 py-1 rounded">
                        {database.ro_username}
                      </span>
                      <Button
                        size="sm"
                        onClick={() =>
                          copyToClipboard(
                            database.ro_username,
                            "Read Only username"
                          )
                        }
                      >
                        <Copy className="w-3 h-3" />
                      </Button>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-mono bg-muted px-2 py-1 rounded">
                        {showPasswords.ro
                          ? database.ro_password
                          : maskPassword(database.ro_password)}
                      </span>
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => togglePasswordVisibility("ro")}
                        >
                          {showPasswords.ro ? (
                            <EyeOff className="w-3 h-3" />
                          ) : (
                            <Eye className="w-3 h-3" />
                          )}
                        </Button>
                        <Button
                          size="sm"
                          onClick={() =>
                            copyToClipboard(
                              database.ro_password,
                              "Read Only password"
                            )
                          }
                        >
                          <Copy className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="border-t border-gray-200"></div>

            {/* Connection Strings */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <ExternalLink className="w-5 h-5" />
                <h3 className="text-lg font-semibold">Connection Strings</h3>
              </div>

              {connectionStrings && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Super Admin</Label>
                    <div className="flex gap-2">
                      <Input
                        value={maskConnectionString(
                          connectionStrings.sa,
                          showConnectionPasswords.sa
                        )}
                        readOnly
                        className="text-xs"
                      />
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() =>
                            toggleConnectionPasswordVisibility("sa")
                          }
                        >
                          {showConnectionPasswords.sa ? (
                            <EyeOff className="w-3 h-3" />
                          ) : (
                            <Eye className="w-3 h-3" />
                          )}
                        </Button>
                        <Button
                          size="sm"
                          onClick={() =>
                            copyToClipboard(
                              connectionStrings.sa,
                              "Super Admin connection string"
                            )
                          }
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Read/Write</Label>
                    <div className="flex gap-2">
                      <Input
                        value={maskConnectionString(
                          connectionStrings.rw,
                          showConnectionPasswords.rw
                        )}
                        readOnly
                        className="text-xs"
                      />
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() =>
                            toggleConnectionPasswordVisibility("rw")
                          }
                        >
                          {showConnectionPasswords.rw ? (
                            <EyeOff className="w-3 h-3" />
                          ) : (
                            <Eye className="w-3 h-3" />
                          )}
                        </Button>
                        <Button
                          size="sm"
                          onClick={() =>
                            copyToClipboard(
                              connectionStrings.rw,
                              "Read/Write connection string"
                            )
                          }
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Read Only</Label>
                    <div className="flex gap-2">
                      <Input
                        value={maskConnectionString(
                          connectionStrings.ro,
                          showConnectionPasswords.ro
                        )}
                        readOnly
                        className="text-xs"
                      />
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() =>
                            toggleConnectionPasswordVisibility("ro")
                          }
                        >
                          {showConnectionPasswords.ro ? (
                            <EyeOff className="w-3 h-3" />
                          ) : (
                            <Eye className="w-3 h-3" />
                          )}
                        </Button>
                        <Button
                          size="sm"
                          onClick={() =>
                            copyToClipboard(
                              connectionStrings.ro,
                              "Read Only connection string"
                            )
                          }
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="border-t border-gray-200"></div>

            {/*Shadow Database Connection Strings */}

            <div className="space-y-3">
              {database.databaseClass == "DEV" && (
                <div className="flex items-center gap-2">
                  <ExternalLink className="w-5 h-5" />
                  <h3 className="text-lg font-semibold">
                    Shadow Database Connection
                  </h3>
                  <a
                    className="flex items-center gap-1 text-blue-800 cursor-pointer"
                    target="_blank"
                    href="https://www.prisma.io/docs/orm/prisma-migrate/understanding-prisma-migrate/shadow-database"
                  >
                    (Required if using Prisma) Why we need it{" "}
                    <ShieldQuestionIcon color="blue" />
                  </a>
                </div>
              )}

              {!shadowConnectionStrings?.sa &&
                database.databaseClass == "DEV" && (
                  <>
                    <Button onClick={() => createShadowDatabase()}>
                      Create Shadow Database
                    </Button>
                  </>
                )}

              {shadowConnectionStrings?.sa &&
                database.databaseClass == "DEV" && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">
                        Connection String
                      </Label>
                      <div className="flex gap-2">
                        <Input
                          value={maskConnectionString(
                            shadowConnectionStrings.sa,
                            showShadowConnectionPasswords.sa
                          )}
                          readOnly
                          className="text-xs"
                        />
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() =>
                              toggleShadowConnectionPasswordVisibility("sa")
                            }
                          >
                            {showShadowConnectionPasswords.sa ? (
                              <EyeOff className="w-3 h-3" />
                            ) : (
                              <Eye className="w-3 h-3" />
                            )}
                          </Button>
                          <Button
                            size="sm"
                            onClick={() =>
                              copyToClipboard(
                                shadowConnectionStrings.sa,
                                "Super Admin connection string"
                              )
                            }
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Username</Label>
                      <div className="space-y-1">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-mono bg-muted px-2 py-1 rounded">
                            {shadowDatabaseDetails.sa_username}
                          </span>
                          <Button
                            size="sm"
                            onClick={() =>
                              copyToClipboard(
                                shadowDatabaseDetails.sa_username,
                                "Read Only username"
                              )
                            }
                          >
                            <Copy className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Password</Label>
                      <div className="space-y-1">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-mono bg-muted px-2 py-1 rounded">
                            {showShadowPasswords.sa
                              ? shadowDatabaseDetails.sa_password
                              : maskPassword(shadowDatabaseDetails.sa_password)}
                          </span>
                          <div className="flex gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() =>
                                toggleShadowPasswordVisibility("sa")
                              }
                            >
                              {showShadowPasswords.sa ? (
                                <EyeOff className="w-3 h-3" />
                              ) : (
                                <Eye className="w-3 h-3" />
                              )}
                            </Button>
                            <Button
                              size="sm"
                              onClick={() =>
                                copyToClipboard(
                                  shadowDatabaseDetails.sa_password,
                                  "Read Only password"
                                )
                              }
                            >
                              <Copy className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
            </div>

            <div className="border-t border-gray-200"></div>

            {/* Danger Zone */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-500" />
                <h3 className="text-lg font-semibold text-red-600">Danger Zone</h3>
              </div>
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 space-y-4">
                <p className="text-sm text-red-800">
                  Irreversible and destructive actions. Please be certain before proceeding.
                </p>
                
                <div className="space-y-3">
                  {/* Mark for Deletion - Only for active databases */}
                  {database.status === "active" && onDelete && (
                    <div className="flex items-center justify-between p-3 bg-white rounded border border-red-200">
                      <div className="flex-1">
                        <div className="font-medium text-gray-900 mb-1">Mark for Deletion</div>
                        <div className="text-sm text-gray-600">
                          Schedule this database for deletion with a 7-day grace period. All users will be disabled immediately.
                        </div>
                      </div>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => setShowMarkForDeletionDialog(true)}
                        disabled={loading}
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        Mark for Deletion
                      </Button>
                    </div>
                  )}

                  {/* Cancel Deletion - Only for suspended databases */}
                  {database.status === "suspended" && onUndoDelete && (
                    <div className="flex items-center justify-between p-3 bg-white rounded border border-yellow-200">
                      <div className="flex-1">
                        <div className="font-medium text-gray-900 mb-1">Cancel Deletion</div>
                        <div className="text-sm text-gray-600">
                          Cancel the scheduled deletion and restore database access. All users will be re-enabled.
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleUndoDelete}
                        disabled={isUndoingDelete || loading}
                      >
                        <RotateCcw
                          className={`w-4 h-4 mr-1 ${
                            isUndoingDelete ? "animate-spin" : ""
                          }`}
                        />
                        {isUndoingDelete ? "Cancelling..." : "Cancel Deletion"}
                      </Button>
                    </div>
                  )}

                  {/* Force Delete - Only for suspended databases */}
                  {database.status === "suspended" && onForceDelete && (
                    <div className="flex items-center justify-between p-3 bg-white rounded border border-red-300">
                      <div className="flex-1">
                        <div className="font-medium text-gray-900 mb-1">Force Delete</div>
                        <div className="text-sm text-red-700">
                          Permanently delete this database immediately. This action cannot be undone and all data will be lost.
                        </div>
                      </div>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => setShowForceDeleteDialog(true)}
                        disabled={isForceDeleting || loading}
                      >
                        <AlertOctagon
                          className={`w-4 h-4 mr-1 ${
                            isForceDeleting ? "animate-spin" : ""
                          }`}
                        />
                        {isForceDeleting ? "Deleting..." : "Force Delete"}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Regenerate Password Confirmation Dialog */}
      {showRegenerateConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-yellow-100 rounded-full">
                  <AlertTriangle className="w-6 h-6 text-yellow-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">
                    Regenerate Passwords
                  </CardTitle>
                  <p className="text-sm text-gray-600">
                    This action cannot be undone
                  </p>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-yellow-800">
                    <p className="font-medium mb-1">⚠️ Warning</p>
                    <ul className="space-y-1 text-xs">
                      <li>• All existing database passwords will be changed</li>
                      <li>
                        • Applications using current credentials will lose
                        access
                      </li>
                      <li>• You'll need to update all connection strings</li>
                      <li>• This action is irreversible</li>
                    </ul>
                  </div>
                </div>
              </div>

              <p className="text-sm text-gray-600">
                Are you sure you want to regenerate all passwords for database{" "}
                <strong>{database.dbname}</strong>?
              </p>

              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setShowRegenerateConfirm(false)}
                  disabled={loading}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={regeneratePasswords}
                  disabled={loading}
                  className="flex-1"
                >
                  {loading ? (
                    <div className="flex items-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Regenerating...
                    </div>
                  ) : (
                    "Regenerate Passwords"
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Mark for Deletion Dialog */}
      {showMarkForDeletionDialog && onDelete && (
        <MarkForDeletionDialog
          isOpen={showMarkForDeletionDialog}
          onClose={() => setShowMarkForDeletionDialog(false)}
          onConfirm={handleMarkForDeletion}
          databaseName={database.dbname}
          databaseType={database.dbtype}
        />
      )}

      {/* Force Delete Dialog */}
      {showForceDeleteDialog && onForceDelete && (
        <ForceDeleteDialog
          isOpen={showForceDeleteDialog}
          onClose={() => setShowForceDeleteDialog(false)}
          onConfirm={handleForceDeleteConfirm}
          databaseName={database.dbname}
          databaseType={database.dbtype}
        />
      )}
    </>
  );
};

export default DatabaseDetails;
